import { useEffect, useState } from 'react';
import { departmentsAPI, usersAPI, attendanceAPI } from '../lib/api';
import { Settings as SettingsIcon, Building2, Plus, Loader2, Users, Trash2, UserPlus, Shield, BookLock, CalendarClock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const ROLES = [
  { value: 'admin', label: 'Admin', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  { value: 'hr_manager', label: 'HR Manager', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  { value: 'line_manager', label: 'Line Manager', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  { value: 'employee', label: 'Employee', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  { value: 'kiosk_user', label: 'Kiosk User', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
];

export default function Settings() {
  const [departments, setDepartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showAddDept, setShowAddDept] = useState(false);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');
  const [newUser, setNewUser] = useState({ email: '', password: '', full_name: '', role: 'employee' });
  const [savingUser, setSavingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [togglingMaster, setTogglingMaster] = useState(null);
  const [season, setSeason] = useState({ winter_start_month: 5, winter_end_month: 8 });
  const [savingSeason, setSavingSeason] = useState(false);

  const MONTHS = [
    { v: 1, l: 'January' }, { v: 2, l: 'February' }, { v: 3, l: 'March' },
    { v: 4, l: 'April' }, { v: 5, l: 'May' }, { v: 6, l: 'June' },
    { v: 7, l: 'July' }, { v: 8, l: 'August' }, { v: 9, l: 'September' },
    { v: 10, l: 'October' }, { v: 11, l: 'November' }, { v: 12, l: 'December' },
  ];

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
    fetchSeason();
  }, []);

  const fetchSeason = async () => {
    try {
      const res = await attendanceAPI.getSeasonConfig();
      setSeason(res.data);
    } catch (error) {
      // non-blocking
    }
  };

  const handleSaveSeason = async () => {
    setSavingSeason(true);
    try {
      const res = await attendanceAPI.updateSeasonConfig(season);
      setSeason(res.data);
      toast.success('Attendance season updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update season');
    } finally {
      setSavingSeason(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await departmentsAPI.getAll();
      setDepartments(res.data);
    } catch (error) {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await usersAPI.getAll();
      setUsers(res.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDeptName.trim()) {
      toast.error('Please enter a department name');
      return;
    }

    try {
      await departmentsAPI.create(newDeptName);
      toast.success('Department added');
      setShowAddDept(false);
      setNewDeptName('');
      fetchDepartments();
    } catch (error) {
      toast.error('Failed to add department');
    }
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.full_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSavingUser(true);
    try {
      await usersAPI.create(newUser);
      toast.success('User created successfully');
      setShowAddUser(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'employee' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    setDeletingUser(userId);
    try {
      await usersAPI.delete(userId);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeletingUser(null);
    }
  };

  const handleToggleMaster = async (userId, next) => {
    setTogglingMaster(userId);
    // Optimistic update
    setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_master: next } : u)));
    try {
      await usersAPI.setMaster(userId, next);
      toast.success(next ? 'Notebook (Master) access granted' : 'Notebook (Master) access revoked');
    } catch (error) {
      // Revert on failure
      setUsers((prev) => prev.map((u) => (u.id === userId ? { ...u, is_master: !next } : u)));
      toast.error(error.response?.data?.detail || 'Failed to update access');
    } finally {
      setTogglingMaster(null);
    }
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || { label: role, color: 'bg-gray-500/20 text-gray-400' };
    return (
      <Badge variant="outline" className={cn("border", roleConfig.color)}>
        {roleConfig.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 page-transition" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground flex items-center gap-3">
          <SettingsIcon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">Manage application settings and users</p>
      </div>

      {/* Attendance Season Section */}
      <div className="glass-card p-6" data-testid="season-config-section">
        <div className="mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-primary" />
            Attendance Season (Winter Window)
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            During these months the <span className="text-foreground font-medium">Winter Day</span> hours apply
            (Mon–Thu 07:30–16:30, Fri 07:30–13:30). Outside them, the <span className="text-foreground font-medium">Normal Day</span> hours apply
            (Mon–Thu 07:00–17:30). Night shift (20:00–06:00) is unaffected.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-4">
          <div className="space-y-1">
            <Label>Winter starts</Label>
            <Select
              value={String(season.winter_start_month)}
              onValueChange={(v) => setSeason({ ...season, winter_start_month: parseInt(v, 10) })}
            >
              <SelectTrigger className="w-full sm:w-48" data-testid="winter-start-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Winter ends</Label>
            <Select
              value={String(season.winter_end_month)}
              onValueChange={(v) => setSeason({ ...season, winter_end_month: parseInt(v, 10) })}
            >
              <SelectTrigger className="w-full sm:w-48" data-testid="winter-end-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((m) => (
                  <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={handleSaveSeason} disabled={savingSeason} data-testid="save-season-btn">
            {savingSeason ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Save Season
          </Button>
        </div>
      </div>

      {/* User Management Section */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            User Management
          </h3>
          <Button onClick={() => setShowAddUser(true)} data-testid="add-user-btn">
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
        </div>
        
        <div className="table-scroll">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="text-center">Notebook Access</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loadingUsers ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell>{getRoleBadge(u.role)}</TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      {togglingMaster === u.id ? (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                      ) : (
                        <Switch
                          checked={!!u.is_master}
                          onCheckedChange={(v) => handleToggleMaster(u.id, v)}
                          data-testid={`master-toggle-${u.id}`}
                          aria-label="Employee Notebook (Master) access"
                        />
                      )}
                      {u.is_master && (
                        <BookLock className="w-4 h-4 text-orange-400" title="Master User — Employee Notebook access" />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeleteUser(u.id)}
                      disabled={deletingUser === u.id}
                      data-testid={`delete-user-${u.id}`}
                    >
                      {deletingUser === u.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Departments Section */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Building2 className="w-5 h-5 text-primary" />
            Departments
          </h3>
          <Button onClick={() => setShowAddDept(true)} data-testid="add-department-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Department
          </Button>
        </div>
        
        <div className="table-scroll">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Department Name</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                  Loading...
                </TableCell>
              </TableRow>
            ) : departments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="text-center py-8 text-muted-foreground">
                  No departments found
                </TableCell>
              </TableRow>
            ) : (
              departments.map((dept) => (
                <TableRow key={dept.id}>
                  <TableCell className="font-medium">{dept.name}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {dept.created_at ? new Date(dept.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Add User Modal */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-primary" />
              Add New User
            </DialogTitle>
            <DialogDescription>
              Create a new user account with the specified role
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={newUser.full_name}
                onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })}
                placeholder="Enter full name"
                data-testid="input-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="Enter email address"
                data-testid="input-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                placeholder="Enter password"
                data-testid="input-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role *</Label>
              <Select 
                value={newUser.role} 
                onValueChange={(v) => setNewUser({ ...newUser, role: v })}
              >
                <SelectTrigger data-testid="select-user-role">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4" />
                        {role.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={savingUser} data-testid="save-user-btn">
              {savingUser ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Department Modal */}
      <Dialog open={showAddDept} onOpenChange={setShowAddDept}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Department</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Department Name</Label>
              <Input
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="Enter department name"
                data-testid="input-department-name"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDept(false)}>Cancel</Button>
            <Button onClick={handleAddDepartment} data-testid="save-department-btn">Add Department</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
